import { Injectable } from '@angular/core';
import { Store, StoreConfig } from '@datorama/akita';
import { TeethDiagramState } from '../models/teeth-diagram';
import { defaultTeethNumbers } from '../models/teeth-numbering';
import { TeethNumberingSystem } from '../models/teeth-numbering-system.enum';
import { getNewTooth, Tooth } from '../models/tooth';
import { UnitTypes } from '../models/unit-type.enum';

export type PartialToothUpdate = Partial<Tooth> & { ToothID?: never };

const createNewJaw = (teethNums: number[]): Tooth[] =>
	teethNums.map((toothId: number) => {
		return {
			ToothID: toothId,
			UnitTypeID: UnitTypes.Regular
		};
	});

export const createInitialState = (): TeethDiagramState => ({
	teethNumberingSystem: TeethNumberingSystem.ADA,
	teeth: {
		upperJaw: createNewJaw(defaultTeethNumbers.upperJaw),
		lowerJaw: createNewJaw(defaultTeethNumbers.lowerJaw)
	},
	copiedToothId: null
});

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'teeth-diagram', resettable: true })
export class TeethDiagramStore extends Store<TeethDiagramState> {
	constructor() {
		super(createInitialState());
	}

	updateTeeth({ teethToUpdate }: { teethToUpdate: Tooth[] }) {
		const { upperJaw, lowerJaw } = this.getUpdatedTeethSync({ teethToUpdate });

		this.update({ teeth: { upperJaw, lowerJaw } });
	}

	/**
	 * Batch update teeth by IDs with partial Tooth properties.
	 * More convenient than updateTeeth when you have IDs and partial updates.
	 * Time complexity: O(n) where n is the total number of teeth.
	 * @param toothIds - Array of tooth IDs to update
	 * @param partialToothUpdate - Partial tooth properties to merge into each tooth
	 */
	updateTeethByIds(toothIds: number[], partialToothUpdate: PartialToothUpdate): void {
		if (toothIds.length === 0) {
			return;
		}

		const currentState = this.getValue();
		const idsToUpdate = new Set(toothIds);

		const upperJaw = currentState.teeth.upperJaw.map(tooth =>
			tooth.ToothID && idsToUpdate.has(tooth.ToothID) ? { ...tooth, ...partialToothUpdate } : tooth
		);

		const lowerJaw = currentState.teeth.lowerJaw.map(tooth =>
			tooth.ToothID && idsToUpdate.has(tooth.ToothID) ? { ...tooth, ...partialToothUpdate } : tooth
		);

		this.update({ teeth: { upperJaw, lowerJaw } });
	}

	resetIndicationByIds(toothIds: number[], indicationId: UnitTypes): void {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/naming-convention
		const { ToothID, ...rest } = getNewTooth({ UnitTypeID: indicationId });

		this.updateTeethByIds(toothIds, rest);
	}

	getUpdatedTeethSync({ teethToUpdate, fromNewState = false }: { teethToUpdate: Tooth[]; fromNewState?: boolean }): {
		upperJaw: Tooth[];
		lowerJaw: Tooth[];
	} {
		let upperJaw = fromNewState ? [...createNewJaw(defaultTeethNumbers.upperJaw)] : [...this.getValue().teeth.upperJaw];
		let lowerJaw = fromNewState ? [...createNewJaw(defaultTeethNumbers.lowerJaw)] : [...this.getValue().teeth.lowerJaw];

		teethToUpdate?.forEach(toothToUpdate => {
			const indexToUpdateUpper = upperJaw.findIndex(tooth => toothToUpdate.ToothID === tooth.ToothID);
			const indexToUpdateLower = lowerJaw.findIndex(tooth => toothToUpdate.ToothID === tooth.ToothID);

			if (indexToUpdateUpper > -1) {
				upperJaw = [...upperJaw.slice(0, indexToUpdateUpper), toothToUpdate, ...upperJaw.slice(indexToUpdateUpper + 1)];
			}
			if (indexToUpdateLower > -1) {
				lowerJaw = [...lowerJaw.slice(0, indexToUpdateLower), toothToUpdate, ...lowerJaw.slice(indexToUpdateLower + 1)];
			}
		});

		return { upperJaw, lowerJaw };
	}

	resetTeeth(): void {
		this.update({
			teeth: {
				upperJaw: createNewJaw(defaultTeethNumbers.upperJaw),
				lowerJaw: createNewJaw(defaultTeethNumbers.lowerJaw)
			},
			copiedToothId: null
		});
	}
}
